import React, { useEffect, useState } from "react";
import closeIcon from "../../assets/cross.svg";
import { schoolService } from "../../services/schoolService";
import { inviteEmailError } from "../../lib/email";

const InputBox = ({ label, name, value, onChange, placeholder, type = "text", disabled = false }) => (
  <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
    <label htmlFor={name} className="text-gray-400 text-sm font-medium">
      {label}
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="text-black text-base font-medium outline-none bg-transparent disabled:text-gray-500"
    />
  </div>
);

const STATUS_BADGE = {
  PENDING: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
  EXPIRED: { label: "Expired", cls: "bg-red-100 text-red-800" },
  CANCELLED: { label: "Cancelled", cls: "bg-gray-100 text-gray-700" },
  ACCEPTED: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800" },
};

/**
 * Two-mode admin invite modal:
 *
 *   1. Invite (default): no `inviteId` prop. Empty form; submit calls
 *      inviteSchoolStaff to send a fresh invite.
 *
 *   2. View / Edit: `inviteId` provided. On open, fetches the latest
 *      non-accepted invite for the school via
 *      getInvites({ schoolId, excludeAccepted: true }) and prefills the
 *      form. Submit cancels the existing invite and creates a new one
 *      with the updated details. If nothing changed, save just closes
 *      (no-op).
 *
 *   The "edit" semantics rely on cancel-old + issue-new because the
 *   backend has no PATCH /invites/:id endpoint. CANCELLED and EXPIRED
 *   invites skip the cancel step (already terminal).
 */
const InviteSchoolAdminModal = ({ school, inviteId, onClose, onConfirm }) => {
  const isEditMode = Boolean(inviteId);

  const [form, setForm] = useState({
    adminName: "",
    adminTitle: "",
    emailAddress: "",
    schoolRole: "OWNER",
  });
  // Original values captured at fetch time, used to skip the call entirely
  // when the operator opened the modal but changed nothing.
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [inviteStatus, setInviteStatus] = useState(null);

  useEffect(() => {
    if (!isEditMode || !school?.id || !inviteId) return;

    let cancelled = false;
    setFetching(true);
    setError(null);
    schoolService
      .getInvites({ schoolId: school.id, excludeAccepted: true })
      .then((invites) => {
        if (cancelled) return;
        const invite = invites.find((i) => i.id === inviteId);
        if (!invite) {
          setError(
            "Invite not found. It may have just been accepted or removed — close this dialog and refresh the list.",
          );
          return;
        }
        const next = {
          adminName: invite.name || "",
          adminTitle: invite.title || "",
          emailAddress: invite.email || "",
          schoolRole: invite.schoolRole || "OWNER",
        };
        setForm(next);
        setOriginal(next);
        setInviteStatus(invite.status);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Failed to load invite details.");
        }
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, school?.id, inviteId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Field-level email check (mirrors the server guard). Blocks submit and
  // shows an inline hint before the operator can fire a doomed invite.
  const emailError = inviteEmailError(form.emailAddress);

  const isValid =
    form.adminName.trim() && form.adminTitle.trim() && form.emailAddress.trim() && !emailError;

  const hasChanges = !original
    ? true
    : form.adminName.trim() !== original.adminName ||
      form.adminTitle.trim() !== original.adminTitle ||
      form.emailAddress.trim() !== original.emailAddress ||
      form.schoolRole !== original.schoolRole;

  const handleSave = async () => {
    if (!isValid || loading || !school?.id) return;

    if (isEditMode && !hasChanges) {
      // No-op: operator opened the modal but changed nothing.
      onClose();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // In edit mode, cancel the existing invite first — unless it's
      // already in a terminal state (CANCELLED/EXPIRED have no effect to
      // cancel further, and POST .../cancel on those may 400).
      if (isEditMode && inviteStatus === "PENDING") {
        try {
          await schoolService.cancelInvite(inviteId);
        } catch (err) {
          // If the cancel fails (e.g. race with acceptance), surface it
          // and stop — we don't want two live invites for the same
          // school.
          setError(err?.message || "Failed to cancel the existing invite.");
          setLoading(false);
          return;
        }
      }

      const response = await schoolService.inviteSchoolStaff(school.id, {
        email: form.emailAddress.trim(),
        role: form.schoolRole,
        name: form.adminName.trim(),
        title: form.adminTitle.trim(),
      });

      if (!response.success) {
        setError(response.error?.detail || response.message);
        return;
      }
      onConfirm?.(school);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to send invite. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const headerTitle = isEditMode ? "View / Edit Admin Invite" : "Invite School Admin";
  const submitLabel = loading
    ? isEditMode
      ? "Saving..."
      : "Sending..."
    : isEditMode
      ? hasChanges
        ? "Save & resend invite"
        : "Close"
      : "Send Invite";

  const badge = inviteStatus ? STATUS_BADGE[inviteStatus] : null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="w-[560px] bg-white rounded-[20px] overflow-hidden font-['Manrope']">
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">{headerTitle}</h2>
          <button type="button" onClick={onClose} className="p-1">
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-8">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* School is fixed — the row that opened this modal owns the
              context. Status badge shown in edit mode so the operator
              knows what they're working with. */}
          <div className="px-4 py-3 bg-blue-50 rounded-[10px] border border-blue-100 flex justify-between items-start gap-3">
            <div className="flex flex-col">
              <p className="text-gray-500 text-xs font-medium">School</p>
              <p className="text-gray-900 text-base font-semibold">{school?.name}</p>
            </div>
            {isEditMode && badge && (
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.cls}`}>
                {badge.label}
              </span>
            )}
          </div>

          {fetching ? (
            <div className="text-gray-500 text-sm font-medium text-center py-6">
              Loading invite details...
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <InputBox
                  label="Admin Name"
                  name="adminName"
                  value={form.adminName}
                  onChange={handleChange}
                  placeholder="e.g., John Doe"
                />
                <InputBox
                  label="Admin Title"
                  name="adminTitle"
                  value={form.adminTitle}
                  onChange={handleChange}
                  placeholder="e.g., Principal or Head of Department"
                />
                <div className="flex flex-col gap-1">
                  <InputBox
                    label="Email Address"
                    name="emailAddress"
                    value={form.emailAddress}
                    onChange={handleChange}
                    type="email"
                    placeholder="e.g., admin@school.edu"
                  />
                  {emailError && <p className="px-1 text-sm text-red-500">{emailError}</p>}
                </div>
                <div className="px-4 py-3.5 bg-white rounded-[10px] shadow-[0px_0px_10px_rgba(0,0,0,0.05)] border border-gray-300 flex flex-col gap-1.5">
                  <label htmlFor="schoolRole" className="text-gray-400 text-sm font-medium">
                    Initial Role
                  </label>
                  <select
                    id="schoolRole"
                    name="schoolRole"
                    value={form.schoolRole}
                    onChange={handleChange}
                    className="text-black text-base font-medium outline-none bg-transparent"
                  >
                    <option value="OWNER">Owner</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
              </div>

              {isEditMode && hasChanges && inviteStatus === "PENDING" && (
                <p className="text-gray-500 text-xs">
                  Saving will cancel the current pending invite and send a
                  fresh one with the updated details.
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-11 px-4 text-[16px] font-medium text-gray-900 border border-blue-500 rounded-lg hover:bg-blue-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!isValid || loading || fetching}
                  className="h-11 px-4 text-[16px] font-medium text-white rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteSchoolAdminModal;
